import {
  Directive, ElementRef, Input, OnDestroy, OnChanges, SimpleChanges,
  AfterViewInit, TemplateRef, ApplicationRef, EmbeddedViewRef,
  Inject, PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import Tooltip from 'bootstrap/js/dist/tooltip';

@Directive({
  selector: '[tooltip]', // dùng: [tooltip]="..."
  standalone: false
})
export class TooltipDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('tooltip') content: string | TemplateRef<any> = '';
  @Input() tooltipCtx: any; // context khi dùng TemplateRef
  @Input() placement: 'top' | 'bottom' | 'left' | 'right' | 'auto' = 'top';
  @Input() trigger = 'hover focus';
  @Input() container: string | false = 'body';
  @Input() customClass?: string | ((el: HTMLElement) => string);
  @Input() delay?: number | { show: number; hide: number };
  @Input() boundary: any = 'clippingParents';
  @Input() fallbackPlacements?: string[];
  @Input() html = false; // bật HTML nếu truyền string HTML
  @Input() sanitize = true; // nên giữ true
  @Input() allowList?: Record<string, string[]>; // mở rộng whitelist nếu cần

  private instance?: Tooltip;
  private viewRef?: EmbeddedViewRef<any>;
  private readonly isBrowser: boolean;

  constructor(
    private host: ElementRef<HTMLElement>,
    private appRef: ApplicationRef,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit() {
    if (this.isBrowser) {
      this.init();
    }
  }

  ngOnChanges(_: SimpleChanges) {
    if (this.isBrowser && this.instance) {
      this.refresh();
    }
  }

  ngOnDestroy() {
    this.disposeView();
    this.instance?.dispose();
  }

  // API điều khiển thủ công (nếu cần)
  public show() { this.instance?.show(); }
  public hide() { this.instance?.hide(); }
  public toggle() { this.instance?.toggle(); }

  private init() {
    this.instance?.dispose();
    this.instance = new Tooltip(this.host.nativeElement, this.buildOptions());
  }

  private refresh() {
    this.init();
  }

  private buildOptions() {
    const needsHtml = this.html || this.content instanceof TemplateRef;
    const title = needsHtml
      ? this.renderToHtml()
      : (typeof this.content === 'string' ? this.content : '');

    const opts: any = {
      title,
      placement: this.placement,
      trigger: this.trigger,
      container: this.container,
      boundary: this.boundary,
      html: needsHtml,
      sanitize: this.sanitize
    };

    // Chỉ thêm option nếu có giá trị để tránh lỗi type
    if (this.customClass !== undefined) opts.customClass = this.customClass;
    if (this.delay !== undefined) opts.delay = this.delay;
    if (this.fallbackPlacements !== undefined) opts.fallbackPlacements = this.fallbackPlacements;
    if (this.allowList !== undefined) opts.allowList = this.allowList;

    return opts;
  }

  private renderToHtml(): string {
    if (typeof this.content === 'string') return this.content;

    this.disposeView();

    const container = document.createElement('div');

    if (this.content instanceof TemplateRef) {
      // TRUYỀN CONTEXT vào đây (nếu không có thì dùng {})
      this.viewRef = this.content.createEmbeddedView(this.tooltipCtx || {});
      this.appRef.attachView(this.viewRef);

      // RẤT QUAN TRỌNG: chạy change detection để *ngIf, *ngFor render
      this.viewRef.detectChanges();

      this.viewRef.rootNodes.forEach((node: any) => {
        container.appendChild(node.cloneNode(true));
      });

      this.appRef.detachView(this.viewRef);
      this.viewRef.destroy();
      this.viewRef = undefined;
    }

    return container.innerHTML;
  }


  private disposeView() {
    if (this.viewRef) {
      this.appRef.detachView(this.viewRef);
      this.viewRef.destroy();
      this.viewRef = undefined;
    }
  }
}
